import React, {useState, useContext, useEffect} from "react";
import {I18nReactContext} from "../../../contexts/I18nContext";
import {get, filter} from "lodash";
import {MultiSelectAutocomplete} from "../../../Components/Form/AutoComplete/AutoComplete";
import './SignDocTypeFilter.scss';

/**
 * SignDocTypeFilter - Multi-select filter for SignDoc types
 * 
 * Follows the same pattern as OrganizationFilter, using the existing
 * MultiSelectAutocomplete component for consistency.
 * 
 * @param {object} params - Component parameters
 * @param {object} params.filterModel - Filter model object containing selected types
 * @param {function} params.setFilterModel - Function to update filter model
 * @param {Array} params.availableTypes - Array of available type options {value, text}
 * @param {string} params.label - Optional custom label
 * @returns {JSX.Element} SignDocTypeFilter component
 */
export const SignDocTypeFilter = ({filterModel, setFilterModel, availableTypes = [], label}) => {
    const {i18n} = useContext(I18nReactContext);
    
    // Initialize selected types from filterModel
    const [selectedTypes, setSelectedTypes] = useState(() => {
        const typeValues = get(filterModel, 'filterValues.types', []);
        return availableTypes.filter(type => typeValues.includes(type.value));
    });

    // Update selectedTypes when availableTypes or filterModel.filterValues.types changes
    useEffect(() => {
        const typeValues = get(filterModel, 'filterValues.types', []);
        const newSelectedTypes = availableTypes.filter(type => typeValues.includes(type.value));
        setSelectedTypes(newSelectedTypes);
    }, [availableTypes, filterModel?.filterValues?.types]);

    /**
     * Handles type selection change
     * Updates filter model with selected type values
     * 
     * @param {Array} selectedTypeObjs - Array of selected type objects with {value, text} format
     */
    const onSelectTypesChange = (selectedTypeObjs) => {
        // Prevent empty selections - if user tries to deselect all, show all types
        if (!selectedTypeObjs || selectedTypeObjs.length === 0) {
            selectedTypeObjs = [];
        }
        
        setSelectedTypes(selectedTypeObjs);
        
        const newFilterModel = {
            ...filterModel,
            filterValues: {
                ...filterModel.filterValues,
                types: selectedTypeObjs.map(type => type.value)
            }
        };
        
        setFilterModel(newFilterModel);
    };

    /**
     * Filters types based on search query for multi-select autocomplete
     */
    const getFilteredTypes = ({query}) => {
        
        // If no query, return full list
        if (!query || query.trim().length === 0) {
            return availableTypes;
        }
        
        // Filter types based on search query
        return filter(availableTypes, type => 
            type.text.toLowerCase().includes(query.toLowerCase())
        );
    };

    return (
        <div className="searchFilter">
            <MultiSelectAutocomplete
                label={label || String(i18n.translate('LearningManagementPage.filterByType'))}
                fetchOptions={getFilteredTypes}
                initialSelectedValues={selectedTypes}
                onSelectionChange={onSelectTypesChange}
                enableSelectAll={false}
                refreshTrigger={availableTypes.length}
            />
        </div>
    );
};

